import {HttpHeaders, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {ApiService} from '@drl/core/api/api.service';
import {ErrToastSkipHeader} from '@drl/core/interceptors/error.interceptor';
import {NameId} from '@drl/core/models';
import {StoreKeys} from '@drl/core/store/store-keys.enum';
import {SystemStoreFacadeService} from '@drl/core/store/system-store-facade.service';
import {InMemoryStorageService} from 'ngx-webstorage-service';
import {cloneDeep} from 'lodash-es';
import {Observable, of} from 'rxjs';
import {tap} from 'rxjs/operators';

import {ContentAdapterService, FilterModelAdapterService} from './adapters';
import {
  GetCategoriesCommand,
  GetConditionCommand,
  GetContentCommand,
  GetContentCommandById,
  GetGalleryByIdCommand,
  GetGalleryCommand,
  GetSubCategories1Command,
  GetSubCategories2Command,
  GetSubCategories3Command,
} from './commands';
import {ContentModel} from './models';

const defaultLimit = 10;
@Injectable()
export class CMSFacadeService {
  constructor(
    private readonly apiService: ApiService,
    private readonly systemStoreFacade: SystemStoreFacadeService,
    private readonly inMemoryStore: InMemoryStorageService,
    private readonly filterModelAdapterService: FilterModelAdapterService,
    private readonly contentAdapter: ContentAdapterService,
  ) {}

  getCategories(reset = false): Observable<NameId[]> {
    const categoriesInStore = this.inMemoryStore.get(StoreKeys.CMS_CATEGORIES);
    if (!reset && categoriesInStore) {
      return of(cloneDeep(categoriesInStore));
    } else {
      const command: GetCategoriesCommand = new GetCategoriesCommand(
        this.apiService,
        this.filterModelAdapterService,
      );
      command.parameters = {
        headers: new HttpHeaders().set(ErrToastSkipHeader, ''),
      };
      return command.execute().pipe(
        tap(categories => {
          this.inMemoryStore.set(
            StoreKeys.CMS_CATEGORIES,
            cloneDeep(categories),
          );
        }),
      );
    }
  }

  getConditions(reset = false): Observable<NameId[]> {
    const conditionsInStore = this.inMemoryStore.get(StoreKeys.CMS_CONDITIONS);
    if (!reset && conditionsInStore) {
      return of(cloneDeep(conditionsInStore));
    } else {
      const command: GetConditionCommand = new GetConditionCommand(
        this.apiService,
        this.filterModelAdapterService,
      );
      command.parameters = {
        headers: new HttpHeaders().set(ErrToastSkipHeader, ''),
      };
      return command.execute().pipe(
        tap(conditions => {
          this.inMemoryStore.set(
            StoreKeys.CMS_CONDITIONS,
            cloneDeep(conditions),
          );
        }),
      );
    }
  }

  private getFiltersToQuery(property, array, queryStr) {
    if (array && array.length > 0) {
      array.map(value => {
        queryStr = `${queryStr}&${property}=${value}`;
      });
    }
    return queryStr;
  }

  private getFilters(limit, offset, rest, modelName) {
    const [
      title,
      conditions,
      categories,
      subcategories1,
      subcategories2,
      subcategories3,
      types,
    ] = rest;
    let queryStr = `_limit=${limit}&_start=${offset}&isPublished=true&_sort=updated_at:DESC`;
    if (title && title.length > 0) {
      if (modelName === 'gallery') {
        queryStr = `${queryStr}&Title_contains=${title}`;
      } else {
        queryStr = `${queryStr}&title_contains=${title}`;
      }
    }
    queryStr = this.getFiltersToQuery('condition', conditions, queryStr);
    queryStr = this.getFiltersToQuery('category', categories, queryStr);
    queryStr = this.getFiltersToQuery('Type', types, queryStr);
    queryStr = this.getFiltersToQuery('subcategory1', subcategories1, queryStr);
    queryStr = this.getFiltersToQuery('subcategory2', subcategories2, queryStr);
    queryStr = this.getFiltersToQuery('subcategory3', subcategories3, queryStr);
    return queryStr;
  }

  getContents(
    limit = defaultLimit,
    offset = 0,
    ...rest
  ): Observable<ContentModel[]> {
    const command: GetContentCommand = new GetContentCommand(
      this.apiService,
      this.contentAdapter,
    );
    command.parameters = {
      query: new HttpParams().set(
        'filter',
        this.getFilters(limit, offset, rest, 'content'),
      ),
    };
    return command.execute();
  }

  getContentById(id): Observable<ContentModel> {
    const command: GetContentCommandById = new GetContentCommandById(
      this.apiService,
      this.contentAdapter,
      id,
    );
    return command.execute();
  }

  getGalleryById(id): Observable<ContentModel> {
    const command: GetGalleryByIdCommand = new GetGalleryByIdCommand(
      this.apiService,
      this.contentAdapter,
      id,
    );
    return command.execute();
  }

  getGalleries(
    limit = defaultLimit,
    offset = 0,
    ...rest
  ): Observable<ContentModel[]> {
    const command: GetGalleryCommand = new GetGalleryCommand(
      this.apiService,
      this.contentAdapter,
    );
    command.parameters = {
      query: new HttpParams().set(
        'filter',
        this.getFilters(limit, offset, rest, 'gallery'),
      ),
    };
    return command.execute();
  }

  getSubCategories1(reset = false): Observable<NameId[]> {
    const subCategoriesInStore = this.inMemoryStore.get(
      StoreKeys.CMS_SUB_CATEGORIES_1,
    );
    if (!reset && subCategoriesInStore) {
      return of(cloneDeep(subCategoriesInStore));
    } else {
      const command: GetSubCategories1Command = new GetSubCategories1Command(
        this.apiService,
        this.filterModelAdapterService,
      );
      command.parameters = {
        headers: new HttpHeaders().set(ErrToastSkipHeader, ''),
      };
      return command.execute().pipe(
        tap(conditions => {
          this.inMemoryStore.set(
            StoreKeys.CMS_SUB_CATEGORIES_1,
            cloneDeep(conditions),
          );
        }),
      );
    }
  }

  getSubCategories2(reset = false): Observable<NameId[]> {
    const subCategoriesInStore = this.inMemoryStore.get(
      StoreKeys.CMS_SUB_CATEGORIES_2,
    );
    if (!reset && subCategoriesInStore) {
      return of(cloneDeep(subCategoriesInStore));
    } else {
      const command: GetSubCategories2Command = new GetSubCategories2Command(
        this.apiService,
        this.filterModelAdapterService,
      );
      command.parameters = {
        headers: new HttpHeaders().set(ErrToastSkipHeader, ''),
      };
      return command.execute().pipe(
        tap(conditions => {
          this.inMemoryStore.set(
            StoreKeys.CMS_SUB_CATEGORIES_2,
            cloneDeep(conditions),
          );
        }),
      );
    }
  }

  getSubCategories3(reset = false): Observable<NameId[]> {
    const subCategoriesInStore = this.inMemoryStore.get(
      StoreKeys.CMS_SUB_CATEGORIES_3,
    );
    if (!reset && subCategoriesInStore) {
      return of(cloneDeep(subCategoriesInStore));
    } else {
      const command: GetSubCategories3Command = new GetSubCategories3Command(
        this.apiService,
        this.filterModelAdapterService,
      );
      command.parameters = {
        headers: new HttpHeaders().set(ErrToastSkipHeader, ''),
      };
      return command.execute().pipe(
        tap(conditions => {
          this.inMemoryStore.set(
            StoreKeys.CMS_SUB_CATEGORIES_3,
            cloneDeep(conditions),
          );
        }),
      );
    }
  }
}
